import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { FilterValues } from 'app/core/interfaces/filtervalues.interface';
import { CDFlorderType, CDFrom, CDTo } from 'app/core/store/contract-details/contract-details.interface';
import { Definition } from 'app/core/store/definitions/definition.interface';
import { FlowDetail } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { getFirst$$, util } from 'app/core/util/util';
import { DeliveryFlowPanelStatus } from 'app/modules/florder/florder-edit-container/florder-edit.interface';
import { FlorderEditDeliveryComponent } from 'app/modules/florder/florder-edit-delivery/florder-edit-delivery.component';
import { appSettings } from '../../../appSettings';
import { AzureMonitoringService } from '../../../core/services/AzureMonitoringService';

@Component({
  selector: 'app-flow-edit-delivery',
  templateUrl: './flow-edit-delivery.component.html',
  styleUrls: ['../../florder/florder-edit-delivery/florder-edit-delivery.component.scss', 'flow-edit-delivery.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush // don't always check for changes, only when input changed (but there's no input here)

  animations: [
    trigger('growInOut', [
      transition(':enter', [style({ height: 0, opacity: 0 }), animate('400ms', style({ height: '50px', opacity: 1 }))]),
      transition(':leave', [animate(400, style({ height: 0, opacity: 0 }))]),
    ]),
  ],
})
export class FlowEditDeliveryComponent extends FlorderEditDeliveryComponent implements OnInit, OnChanges {
  wholesalers: Definition[] = [];
  senderRefIsRequired: boolean = false;
  showSenderRefMsg: boolean = false;
  toError: String = '';
  fromError: String = '';
  wholesalerValid: boolean = false;
  wholesalerFilledIn: boolean = false;
  wholesalerRequired: boolean = false;
  wholesalerErrorObject: { [key: string]: boolean } = (!this.wholesalerValid && this.wholesalerRequired && this.wholesalers.length > 0)
    ? { 'ERRORS.ORDER.CREATE.WHOLESALER_EMPTY': true }
    : null;
  senderRefEmptyErrorObject: { [key: string]: boolean } = null;
  labelWidth = appSettings.LABEL_WIDTH_WIDE;

  @Input() panelStatus: DeliveryFlowPanelStatus;
  @Input() flowDetail: FlowDetail;
  @Input() filterValues: FilterValues;
  @Input() flowRegistrationTypes: CDFlorderType[];
  @Input() flowHandshakeTypes: CDFlorderType[];
  @Input() isCreateFlow = false;

  @Output() resetMaterialsAndPlanning: EventEmitter<null> = new EventEmitter();
  @Output() emptyContract = new EventEmitter<void>();
  @Output('flowTypeSelected') flowTypeSelectedEventEmitter: EventEmitter<string> = new EventEmitter();

  private azureMonitoringService = inject(AzureMonitoringService);

  ngOnInit(): void {
    super.ngOnInit();
    this.initFlowTypes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.busy && this.deliveryForm) {
      if (this.busy) {
        this.deliveryForm.get('type').disable();
      } else {
        this.deliveryForm.get('type').enable();
      }
    }
    if (changes.flowDetail && changes.flowDetail.previousValue && changes.flowDetail.previousValue.etmOrderNumber && !changes.flowDetail.currentValue.etmOrderNumber) {
      this.ngOnInit();
    }
    if (changes.flowTypes) {
      this.initFlowTypes();
    }
    if (changes.deliveryMethod) {
      this.ngOnInit();
    }
    if (changes.flowRegistrationTypes) {
      this.initFlowTypes();
      if (this.panelStatus.isNew && this.flowRegistrationTypes?.length === 0) {
        this.emptyContract.emit();
      }
    }

    if (changes.to) {
      if (!this.poNumberMandatory) {
        const custRefControl = this.deliveryForm.get('customerReferenceNumber');
        custRefControl.setErrors(null);
      }
    }
  }

  flowTypeChanged() {
    if (!this.busy) {
      this.deliveryForm.controls['from'].setValue({});
      this.deliveryForm.controls['to'].setValue({});
      this.deliveryForm.controls['wholesaler'].setValue({});
      this.deliveryForm.controls['customerReferenceNumber'].setValue('');
      this.deliveryForm.controls['senderReferenceNumber'].setValue('');
      this.deliveryForm.controls['transporter'].setValue('');
      this.deliveryForm.controls['licensePlate'].setValue('');
      this.deliveryForm.controls['remarks'].setValue('');
      if (this.isCreateFlow) {
        if (this.deliveryForm.value.type) {
          this.flowTypeSelectedEventEmitter.emit(this.deliveryForm.value.type);
        } else {
          this.azureMonitoringService?.logEvent(`FlowEditDeliveryComponent.flowTypeChanged(): empty flowTypeId '${this.deliveryForm.value.type}'`);
        }
      }


      this.resetMaterialsAndPlanning.emit();
      this.setFroms();
    }
  }

  initFlowTypes() {
    if (this.getFlowTypes().length > 0 && this.deliveryForm) {
      this.setFroms();
    } else {
      this.froms = [];
      this.tos = [];
      this.wholesalers = [];
    }

    // If only 1 orderType: select it and trigger load of next part of contract
    if (this.getFlowTypes().length === 1 && this.deliveryForm && !this.deliveryForm.value.type && this.isCreateFlow) {
      const flowTypeId = this.getFlowTypes()[0].id;
      if (flowTypeId) {
        this.deliveryForm.get('type').setValue(flowTypeId, { emitEvent: false });
        this.flowTypeSelectedEventEmitter.emit(flowTypeId);
      } else {
        this.azureMonitoringService?.logEvent(`FlowEditDeliveryComponent.initFlowTypes(): empty flowTypeId '${flowTypeId}'`);
      }
    }

    this.cd.markForCheck(); // Needed when changeDetectionStrategy == onPush
  }

  setFroms(): void {
    this.froms = util.setSelectElements<CDFrom>({
      selectedId: this.deliveryForm.get('type').value,
      containerArray: this.getFlowTypes(),
      childSelector: flowType => (flowType.shippingConditions.length && flowType.shippingConditions[0].froms) || [],
      childControls: this.deliveryForm.get('from'),
      defaultAll: false,
      returnObject: true,
      cascade: this.setTos.bind(this),
      getTitle: from => util.generateAddressString(this.filterValues.locations, from.id, this.translate.instant('SHARED.LABELS.NO_ADDRESS')),
      nameForLogging: 'froms'
    });
    this.cd.markForCheck(); // Needed when changeDetectionStrategy == onPush
  }

  fromChanged(): void {
    this.setTos();
  }

  private setTos(): void {
    const containerParent = this.getFlowTypes().find(flowtype => flowtype.id === this.deliveryForm.get('type').value);
    if (containerParent) {
      this.tos = util.setSelectElements<CDTo>({
        selectedId: this.deliveryForm.get('from').value && this.deliveryForm.get('from').value.id,
        containerArray: (containerParent && containerParent.shippingConditions.length && containerParent.shippingConditions[0].froms) || [],
        childSelector: from => from.tos,
        cascade: this.setClearing.bind(this),
        childControls: [this.deliveryForm.get('to')],
        returnObject: true,
        defaultAll: false,
        getTitle: to => util.generateAddressString(this.filterValues.locations, to.id, this.translate.instant('SHARED.LABELS.NO_ADDRESS')),
        nameForLogging: 'tos'
      });
      this.cd.markForCheck(); // Needed when changeDetectionStrategy == onPush
    }
  }

  protected getTo(): CDTo {
    const flowType = this.getFlowTypes().find(flowtype => flowtype.id === this.deliveryForm.get('type').value);
    const from =
      flowType &&
      flowType.shippingConditions.length &&
      flowType.shippingConditions[0].froms.find(from => this.deliveryForm.get('from').value && from.id === this.deliveryForm.get('from').value.id);
    return from && from.tos.find(to => this.deliveryForm.get('to').value && to.id === this.deliveryForm.get('to').value.id);
  }

  toChanged(): void {
    this.validateSenderRefNr();
    this.validateReceiverRefNr();
    const to = this.getTo();
    this.setClearing(to);
    this.setValidationOnPoReferenceNumber();
    this.poNumberMask = (to && to.poNumber && to.poNumber.example && this.translate.instant('ORDERS.DELIVERY.LABELS.MASK') + to.poNumber.example) || '';
    this.checkWholeSalerAndSenderRef();
  }

  private setClearing(to: CDTo): void {
    // eslint-disable-next-line
    to = to || this.getTo();

    const toClearing = to && to.clearing;
    const clearing = this.deliveryMethod && this.deliveryMethod.clearing ? this.deliveryMethod.clearing : toClearing;

    // Ensure clearing is always a boolean, not a number (0/1)
    (this.deliveryForm.get('clearing') as UntypedFormControl).setValue(!!clearing);

    // set wholesalers
    this.wholesalers = (to && to.wholesaler && to.wholesaler.wholesalers) || [];
    this.wholesalerRequired = to && to.wholesaler && to.wholesaler.mandatory;

    if (this.wholesalerRequired && this.wholesalers.length === 1) {
      // set the field if only one wholesaler
      (this.deliveryForm.get('wholesaler') as UntypedFormControl).setValue(this.wholesalers[0]);
    }

    this.showSenderRefMsg = this.checkShowSenderRefMsg();
    this.senderRefIsRequired = this.checkSenderRefRequired();
  }

  createForm(): void {
    super.createForm();
    this.triggerValidatePONumber();
    const getSenderRefIsRequiredDummyControl = () => {
      const to = this.getTo();
      const toControl = this.deliveryForm.get('to');
      return {
        value: to && to.wholesaler && to.wholesaler.mandatory, // Value to evaluate whether or not to add required validator to control
        valueChanges: {
          subscribe: toControl.valueChanges.subscribe.bind(toControl.valueChanges), // re-evaluate condition when TO changed
        },
      };
    };
    util.conditionalValidation(this.deliveryForm, 'wholesaler', [util.createCondition(getSenderRefIsRequiredDummyControl)], [Validators.required]);

    const checkWholesalerIsFilledIn = () => {
      const wholeSaler = this.deliveryForm.get('wholesaler');
      const wholesalerFilledIn = wholeSaler && wholeSaler.value && wholeSaler.value.name && wholeSaler.value.name !== ' ';
      return {
        value: wholesalerFilledIn,
        valueChanges: {
          subscribe: wholeSaler.valueChanges.subscribe.bind(wholeSaler.valueChanges), // re-evaluate condition when wholesaler field changed
        },
      };
    };
    util.conditionalValidation(this.deliveryForm, 'senderReferenceNumber', [util.createCondition(checkWholesalerIsFilledIn)], [Validators.required]);
    this.checkWholeSalerAndSenderRef();
  }

  protected getControlsConfig() {
    return Object.assign(super.getControlsConfig(), {
      wholesaler: [this.deliveryMethod.wholesaler],
      remarks: [this.deliveryMethod.remarks],
      remarksHandshaker: [this.deliveryMethod.remarksHandshaker],
      clearing: [!!this.deliveryMethod.clearing], // Ensure boolean conversion at initialization
    });
  }

  protected getEtmOrderNumber(): string {
    return this.flowDetail && this.flowDetail.etmOrderNumber;
  }

  nextClickedFn() {
    if (!this.bothRefNrsValidated) {
      this.validateBothRefNrs().then(async (noErrors) => {
        if (noErrors) {
          const pending = await getFirst$$(this.refNrValidationPending);
          if (!pending && this.deliveryForm.valid) {
            this.nextClicked.emit(this.deliveryForm.value);
          }
        }
      });
    }
  }

  private getFlowTypes() {
    const flowTypes = this.flowDetail.handshaker ? this.flowHandshakeTypes : this.flowRegistrationTypes;
    return flowTypes || [];
  }

  private checkWholesalerFilledIn(): boolean {
    const val = this.deliveryForm.get('wholesaler').value;
    return !!(val && val.name && val.name.trim());
  }

  private checkShowSenderRefMsg(): boolean {
    // update boolean for required senderRef field
    this.senderRefIsRequired = this.checkSenderRefRequired();
    const senderReferenceValue = this.deliveryForm.get('senderReferenceNumber').value;
    const senderReferenceIsEmpty = !!!senderReferenceValue;
    return senderReferenceIsEmpty && (this.senderRefIsRequired || this.wholesalerFilledIn);
  }

  private checkSenderRefRequired(): boolean {
    this.wholesalerFilledIn = this.checkWholesalerFilledIn();
    return this.wholesalerFilledIn || this.wholesalerRequired;
  }

  private checkWholeSalerAndSenderRef() {
    this.wholesalerFilledIn = this.checkWholesalerFilledIn();
    // make sender reference field required when a wholesaler is selected
    this.senderRefIsRequired = this.checkSenderRefRequired();
    this.wholesalerValid = this.wholesalerFilledIn;
    this.showSenderRefMsg = this.checkShowSenderRefMsg();
    // Update the error objects dynamically
    this.wholesalerErrorObject = (!this.wholesalerValid && this.wholesalerRequired && this.wholesalers.length > 0)
      ? { 'ERRORS.ORDER.CREATE.WHOLESALER_EMPTY': true }
      : null;
    this.senderRefEmptyErrorObject = this.showSenderRefMsg
      ? { 'ERRORS.ORDER.CREATE.SENDER_REF_IS_EMPTY': true }
      : null;
    this.cd.detectChanges();
  }
}
